<template>
  <div>
    <v-tabs
      v-if="roleTabs.length > 0"
      v-model="roleTab"
      class="mb-3"
      show-arrows
    >
      <v-tab
        v-for="r in roleTabs"
        :key="r.value"
        :value="r.value"
      >
        {{ r.label }}
      </v-tab>
    </v-tabs>

    <div class="d-flex align-center mb-3">
      <v-btn-toggle
        v-model="statusFilter"
        mandatory
        dense
        color="primary"
      >
        <v-btn
          v-for="option in statusFilterOptions"
          :key="option.value"
          :value="option.value"
          small
        >
          {{ option.text }}
        </v-btn>
      </v-btn-toggle>

      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-icon
            small
            class="ml-2 text--secondary"
            v-bind="attrs"
            v-on="on"
          >
            {{ icons.mdiHelpCircleOutline }}
          </v-icon>
        </template>
        <span>
          すべて: 全ての期間を表示<br>
          下書き: 全員が下書きの期間のみ<br>
          確定済み: 全員が確定済みの期間のみ<br>
          混在: 同じ期間に下書きと確定済みが混ざっている期間のみ
        </span>
      </v-tooltip>
    </div>

    <template v-if="isLoading">
      <v-skeleton-loader type="table" />
    </template>

    <template v-else>
      <v-card
        v-for="(group, index) in pagedGroups"
        :key="`${rowKey(group)}-${index}`"
        outlined
        class="group-card mb-3"
        :to="detailRoute(group)"
      >
        <div class="group-card__header d-flex align-center flex-wrap">
          <span class="group-card__period">
            {{ group.periodStart }} 〜 {{ group.periodEnd }}
          </span>

          <span class="text--secondary text-caption ml-3">
            {{ group.total }}件（下書き {{ group.draftCount }} / 確定 {{ group.confirmedCount }}）
          </span>

          <v-chip
            class="ml-3"
            :color="statusColor(group.status)"
            x-small
            label
            dark
          >
            {{ statusLabel(group.status) }}
          </v-chip>

          <v-spacer />

          <span class="text--secondary text-caption mr-2 d-none d-sm-inline">
            最終確定: {{ group.lastConfirmedAt || '—' }}
          </span>

          <v-btn
            v-if="group.status === 'draft'"
            icon
            small
            color="error"
            @click.stop.prevent="openDeleteDialog(group)"
          >
            <v-icon small>
              {{ icons.mdiDelete }}
            </v-icon>
          </v-btn>

          <v-icon
            small
            class="ml-1 text--secondary"
          >
            {{ icons.mdiChevronRight }}
          </v-icon>
        </div>
      </v-card>

      <v-alert
        v-if="filteredGroups.length === 0"
        type="info"
        text
        dense
        class="mb-0"
      >
        該当する確定データがありません。「新規作成」から確定データを作成してください。
      </v-alert>

      <div
        v-if="pageCount > 1"
        class="d-flex justify-center mt-4"
      >
        <v-pagination
          v-model="page"
          :length="pageCount"
          total-visible="7"
        />
      </div>
    </template>

    <v-dialog
      v-model="deleteDialog"
      max-width="420"
    >
      <v-card>
        <v-card-title class="text-h6">
          下書きを削除
        </v-card-title>
        <v-card-text>
          <template v-if="targetGroup">
            この下書き（{{ targetGroup.periodStart }} 〜 {{ targetGroup.periodEnd }} /
            {{ roleLabel(targetGroup.role) }}）を削除します。よろしいですか？
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeDeleteDialog"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            text
            @click="confirmDelete"
          >
            削除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiDelete, mdiChevronRight, mdiHelpCircleOutline,
} from '@mdi/js'
import { groupKey } from '../composables/usePayrollConfirmation'

export default {
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    roleLabel: {
      type: Function,
      required: true,
    },
  },
  emits: ['delete'],
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const statusFilter = ref('all')
    const roleTab = ref(null)

    const deleteDialog = ref(false)
    const targetGroup = ref(null)

    const rowKey = group => groupKey(group)

    // groups に存在するロールからタブを生成する（データの無いロールは出さない）
    const roleTabs = computed(() => {
      const seen = new Map()
      props.groups.forEach(group => {
        if (!seen.has(group.role)) {
          seen.set(group.role, { value: group.role, label: props.roleLabel(group.role) })
        }
      })

      return [...seen.values()]
    })

    // タブ未選択 or 選択中ロールが消えたら先頭ロールに合わせる
    watch(roleTabs, tabs => {
      if (tabs.length > 0 && !tabs.some(t => t.value === roleTab.value)) {
        roleTab.value = tabs[0].value
      }
    }, { immediate: true })

    // カードクリックで期間×ロールの詳細ページへ遷移する
    const detailRoute = group => ({
      name: 'payroll-confirmation-detail',
      params: {
        club_slug: vm.$route.params.club_slug,
        role: group.role,
        periodStart: group.periodStart,
        periodEnd: group.periodEnd,
      },
    })

    const openDeleteDialog = group => {
      targetGroup.value = group
      deleteDialog.value = true
    }

    const closeDeleteDialog = () => {
      deleteDialog.value = false
      targetGroup.value = null
    }

    const confirmDelete = () => {
      if (targetGroup.value) emit('delete', targetGroup.value)
      closeDeleteDialog()
    }

    const statusFilterOptions = [
      { text: 'すべて', value: 'all' },
      { text: '下書き', value: 'draft' },
      { text: '確定済み', value: 'confirmed' },
      { text: '混在', value: 'mixed' },
    ]

    const filteredGroups = computed(() => {
      let list = props.groups
      if (roleTab.value) list = list.filter(group => group.role === roleTab.value)
      if (statusFilter.value !== 'all') list = list.filter(group => group.status === statusFilter.value)

      return list
    })

    // クライアント側ページネーション（groups は全件取得済みなのでフロントで分割表示する）
    const PER_PAGE = 10
    const page = ref(1)
    const pageCount = computed(() => Math.ceil(filteredGroups.value.length / PER_PAGE) || 1)
    const pagedGroups = computed(() => {
      const start = (page.value - 1) * PER_PAGE

      return filteredGroups.value.slice(start, start + PER_PAGE)
    })

    // フィルタ変更時はページを先頭に戻す
    watch([roleTab, statusFilter], () => {
      page.value = 1
    })

    const statusColor = status => {
      switch (status) {
        case 'draft':
          return 'warning'
        case 'confirmed':
          return 'success'
        case 'mixed':
          return 'info'
        default:
          return 'grey'
      }
    }

    const statusLabel = status => {
      switch (status) {
        case 'draft':
          return '下書き'
        case 'confirmed':
          return '確定済み'
        case 'mixed':
          return '混在'
        default:
          return status
      }
    }

    return {
      statusFilter,
      statusFilterOptions,
      roleTab,
      roleTabs,
      filteredGroups,
      page,
      pageCount,
      pagedGroups,
      statusColor,
      statusLabel,
      deleteDialog,
      targetGroup,
      rowKey,
      detailRoute,
      openDeleteDialog,
      closeDeleteDialog,
      confirmDelete,
      icons: {
        mdiDelete,
        mdiChevronRight,
        mdiHelpCircleOutline,
      },
    }
  },
}
</script>

<style scoped>
.group-card__header {
  padding: 12px 16px;
}
.group-card__period {
  font-weight: 600;
  white-space: nowrap;
}
</style>
